import Phaser from 'phaser';
import {
  TILE_SIZE,
  TEXTURE_KEYS,
  DEPTHS,
  COLORS,
  PROP_KEYS,
  BG_FAR_KEYS,
  BG_NEAR_KEYS
} from '../systems/util/constants.js';
import { InputController } from '../systems/input.js';
import { stitchLevel, stitchEndlessLevel } from '../systems/procgen/ChunkStitcher.js';
import { SeededRNG } from '../systems/util/rng.js';
import { ProjectilePool } from '../systems/combat/ProjectilePool.js';
import { HitboxManager } from '../systems/combat/HitboxManager.js';
import { Player } from '../entities/Player.js';
import { Enemy } from '../entities/Enemy.js';
import { ElectricFence } from '../entities/Hazard.js';
import { SaveManager } from '../systems/SaveManager.js';
import { RetroPostFxPipeline } from '../systems/fx/RetroPostFxPipeline.js';
import { submitScore, LEADERBOARD_ENABLED } from '../systems/Leaderboard.js';
import { INK, INK_NUM, PAPER_NUM, MUTED, showLeaderboardOverlay } from '../systems/ui/chrome.js';
import levels from '../../data/levels.json';

// Excludes `endless` — it's not part of the linear campaign order, so it
// should never be picked as "the next level" after beating level-10.
const LEVEL_IDS = Object.keys(levels).filter((id) => !levels[id].endless);

export class Game extends Phaser.Scene {
  constructor() {
    super('Game');
  }

  init(data) {
    this.levelId = data.levelId;
    this.levelDef = levels[this.levelId];
  }

  create() {
    this.paused = false;
    this.levelComplete = false;
    this.startTime = this.time.now;
    this.score = 0;

    const levelData = this.levelDef.endless ? stitchEndlessLevel() : stitchLevel(this.levelDef);
    this.levelData = levelData;

    // Physics bounds extend below the visible level so a fall into a pit is
    // an actual fall (triggering Player's fall-respawn check) rather than
    // being caught by world-bounds collision at the floor line, which would
    // otherwise make that check unreachable and softlock the player.
    this.physics.world.setBounds(0, 0, levelData.widthPx, levelData.heightPx + 300);
    this.cameras.main.setBounds(0, 0, levelData.widthPx, levelData.heightPx);
    this.cameras.main.setBackgroundColor(COLORS.white);

    this._buildParallax(levelData);
    this._buildTiles(levelData);
    this._buildProps(levelData);
    this._buildHazards(levelData);
    this._buildPickups(levelData);
    this._buildExit(levelData);

    this.playerProjectiles = new ProjectilePool(this, {
      textureKey: TEXTURE_KEYS.PLAYER_PROJECTILE,
      ownerTeam: 'player',
      poolSize: 30
    });
    this.enemyProjectiles = new ProjectilePool(this, {
      textureKey: TEXTURE_KEYS.ENEMY_PROJECTILE,
      ownerTeam: 'enemy',
      poolSize: 40
    });

    this.player = new Player(this, levelData.playerStart.x, levelData.playerStart.y, {
      projectilePool: this.playerProjectiles
    });

    // No `allowGravity` passed here, so Phaser's PhysicsGroup defaults it to
    // true — and Group.add() re-applies that default to every member's body
    // via its internal create callback, which silently undid the
    // `setAllowGravity(false)` each Enemy sets on itself in its own
    // constructor (verified against Phaser's PhysicsGroup.js:
    // `createCallbackHandler` runs `body.setAllowGravity(...)` from the
    // group's defaults on every `.add()`, regardless of what the body was
    // already configured with). Enemies that continuously re-set their own
    // y each frame (flightBob drones) never showed it, but a stationary,
    // no-gravity enemy like the turret just fell under gravity the instant
    // it was added to the group. Re-asserting each enemy's real config
    // after `.add()` below fixes it without fighting the group defaults.
    this.enemiesGroup = this.physics.add.group({ runChildUpdate: false });
    this.enemies = [];
    for (const e of levelData.enemies) {
      const enemy = new Enemy(this, e.x, e.y, e.type, { projectilePool: this.enemyProjectiles, target: this.player });
      this.enemiesGroup.add(enemy);
      enemy.body.setAllowGravity(!!enemy.config.gravity);
      this.enemies.push(enemy);
    }

    this.boss = null;
    if (levelData.boss) {
      this.boss = new Enemy(this, levelData.boss.x, levelData.boss.y, levelData.boss.type, {
        projectilePool: this.enemyProjectiles,
        target: this.player
      });
      this.enemiesGroup.add(this.boss);
      this.boss.body.setAllowGravity(!!this.boss.config.gravity);
      this.enemies.push(this.boss);
    }

    this._setupColliders();

    this.inputController = new InputController(this);

    this.cameras.main.startFollow(this.player, true, 0.12, 0.12);
    this.cameras.main.setDeadzone(120, 80);

    if (this.game.renderer.type === Phaser.WEBGL) {
      this.cameras.main.setPostPipeline(RetroPostFxPipeline);
    }

    this._buildHud();
    this._onHpChanged(this.player.hp, this.player.maxHp);

    this.events.on('player-died', this._onPlayerDied, this);
    this.events.on('player-fell', this._onPlayerFell, this);
    this.events.on('player-hp-changed', this._onHpChanged, this);
    this.events.on('player-ammo-changed', this._onAmmoChanged, this);
    this.events.on('enemy-died', this._onEnemyDied, this);
  }

  // -- world construction ----------------------------------------------------

  // Comic-art overhaul: the far/near backgrounds used to be one seamless
  // procedural TileSprite each, manually scrolled via tilePositionX. Now
  // they're built from several distinct hand-drawn building images with no
  // single repeatable unit, so instead they're placed as real world-space
  // Images with Phaser's native scrollFactor doing the parallax — cheaper
  // and simpler than faking it, and it lets each layer cycle through a
  // shuffled sequence of the supplied art instead of repeating one texture.
  _buildParallax(levelData) {
    const rng = new SeededRNG(`${this.levelDef.seed}-bg`);
    const screenW = this.scale.width;
    // Floor is now a single row flush with the bottom (see generate-chunks.mjs).
    const floorTopY = (levelData.heightTiles - 1) * TILE_SIZE;

    const FAR_SCROLL = 0.2;
    const NEAR_SCROLL = 0.5;

    this._layoutParallaxLayer({
      keys: BG_FAR_KEYS,
      rng,
      scrollFactor: FAR_SCROLL,
      targetHeight: levelData.heightPx,
      anchorY: levelData.heightPx,
      depth: DEPTHS.BACKGROUND,
      spanPx: levelData.widthPx * FAR_SCROLL + screenW,
      // Faded more than the near layer — greys it out against the white
      // page background and reads as atmospheric distance.
      alpha: 0.35
    });

    this._layoutParallaxLayer({
      keys: BG_NEAR_KEYS,
      rng,
      scrollFactor: NEAR_SCROLL,
      targetHeight: 192,
      // No extra offset anymore — floorTopY is now the real collision line
      // again (floor art no longer has its own shifted position to match),
      // so this 0.15 fudge would just push it back out of alignment. Retune
      // from 0 if you want it lower/higher.
      anchorY: floorTopY,
      depth: DEPTHS.BACKGROUND + 1,
      spanPx: levelData.widthPx * NEAR_SCROLL + screenW,
      gap: 30,
      alpha: 0.9
    });
  }

  _layoutParallaxLayer({ keys, rng, scrollFactor, targetHeight, anchorY, depth, spanPx, gap = 0, alpha = 1 }) {
    const order = rng.shuffle(keys);
    // Start left of the camera's initial view so panning left at the level
    // start never reveals a bare gap before the first image.
    let x = -spanPx * 0.15;
    let i = 0;
    while (x < spanPx) {
      const key = order[i % order.length];
      i++;
      const img = this.add.image(x, anchorY, key).setOrigin(0, 1).setScrollFactor(scrollFactor).setDepth(depth);
      img.setScale(targetHeight / img.height);
      // The art is black linework on a transparent PNG, so a tint can't
      // lighten it (multiplying black by any color is still black) — alpha
      // against the white page background is what actually greys it out.
      img.setAlpha(alpha);
      x += img.displayWidth + gap;
    }
  }

  _buildTiles(levelData) {
    this.solidGroup = this.physics.add.staticGroup();
    this.platformGroup = this.physics.add.staticGroup();
    this.spikeGroup = this.physics.add.staticGroup();

    // Visual: one bordered tile image per cell, exactly as before — plain
    // (non-physics) Images now, since collision comes from the merged
    // run-bodies below instead. Drawn at the exact same position/size, so
    // this looks identical to when these carried their own bodies.
    const rows = levelData.rows;
    for (let r = 0; r < rows.length; r++) {
      const row = rows[r];
      for (let c = 0; c < row.length; c++) {
        const ch = row[c];
        if (ch === '.') continue;
        const x = c * TILE_SIZE + TILE_SIZE / 2;
        const y = r * TILE_SIZE + TILE_SIZE / 2;
        if (ch === '#') {
          this.add.image(x, y, TEXTURE_KEYS.TILE_SOLID).setDepth(DEPTHS.TILES);
        } else if (ch === '=') {
          this.add.image(x, y, TEXTURE_KEYS.TILE_PLATFORM).setDepth(DEPTHS.TILES);
        } else if (ch === '^') {
          // Fire.png replaces the old procedural red-triangle spike tile —
          // sized to a fixed height (like props/turret) rather than
          // squashed into one tile, and bottom-anchored to the floor
          // surface below this row so the flame base sits on the ground,
          // matching the tile grid's "surface = row-center + 16" convention
          // used everywhere else (Player.js/Enemy.js).
          const spike = this.spikeGroup.create(x, y + TILE_SIZE / 2, TEXTURE_KEYS.HAZARD_FIRE).setDepth(DEPTHS.TILES);
          spike.setOrigin(0.5, 1);
          spike.setScale(48 / spike.height);
          spike.refreshBody();
          spike.setData('damage', 1);
        }
      }
    }

    // Collision: origin is set BEFORE the physics body is attached (via a
    // plain Image + physics.add.existing(..., true), rather than
    // staticGroup.create(), which attaches the body immediately using
    // whatever origin the image has at that instant — still the default
    // 0.5/0.5). That matters because StaticBody.setSize(w, h, false) only
    // changes width/height, it doesn't touch the cached position; and
    // refreshBody() (which does recompute position from the current
    // origin) ALSO resets width/height back to the texture's native size
    // in the process — so it can't be used to fix the origin *after* a
    // custom setSize. Getting the origin right before body-creation avoids
    // needing either.
    const solidBody = (x, y, w, h) => {
      const img = this.add.image(x, y, TEXTURE_KEYS.TILE_SOLID).setOrigin(0, 0).setVisible(false);
      this.physics.add.existing(img, true);
      img.body.setSize(w, h, false);
      this.solidGroup.add(img);
    };

    // The floor is always exactly one continuous row spanning the ENTIRE
    // level width — not detected from the tile grid, but built directly
    // from levelData.widthPx (guaranteed by assertFloorIntact at
    // chunk-authoring time, and independently confirmed with a script
    // against every real level's stitched output). One deterministic body
    // for the single most critical collision surface in the game, with no
    // run-detection logic in the loop that could have an edge-case bug —
    // rather than continuing to debug the previous per-run version, this
    // removes the whole class of possible bug for the floor specifically.
    const floorRow = rows.length - 1;
    solidBody(0, floorRow * TILE_SIZE, levelData.widthPx, TILE_SIZE);

    // Any OTHER solid cells (e.g. the boss arena's bounding side walls)
    // aren't guaranteed contiguous across the whole level the way the
    // floor is, so those still use run-detection — same merged-body
    // approach, just not depended on for the floor itself anymore.
    for (let r = 0; r < floorRow; r++) {
      this._paintRuns(rows[r], r, '#', (x, y, w) => solidBody(x, y, w, TILE_SIZE));
    }

    for (let r = 0; r < rows.length; r++) {
      this._paintRuns(rows[r], r, '=', (x, y, w) => {
        // Platform texture is shorter than a full tile and was always
        // vertically centered within its row (origin 0.5, 0.5) — origin
        // (0, 0.5) here keeps that same vertical centering while aligning
        // the left edge to the run's start, matching where the visual
        // tiles above actually are instead of shifting the surface up.
        const img = this.add
          .image(x, y + TILE_SIZE / 2, TEXTURE_KEYS.TILE_PLATFORM)
          .setOrigin(0, 0.5)
          .setVisible(false);
        this.physics.add.existing(img, true);
        img.body.setSize(w, TILE_SIZE * 0.4, false);
        this.platformGroup.add(img);
      });
    }
  }

  /** Finds contiguous horizontal runs of `ch` in one row and calls onRun(xPx, yPx, widthPx) per run. */
  _paintRuns(row, r, ch, onRun) {
    let start = -1;
    for (let c = 0; c <= row.length; c++) {
      const match = c < row.length && row[c] === ch;
      if (match && start === -1) start = c;
      if (!match && start !== -1) {
        onRun(start * TILE_SIZE, r * TILE_SIZE, (c - start) * TILE_SIZE);
        start = -1;
      }
    }
  }

  // Purely decorative dressing (crates, pipes, signage) — no collision, no
  // gameplay effect. Scattered along the floor line at seeded-random
  // intervals so it's reproducible per level like the chunk stitching, but
  // varied from level to level.
  _buildProps(levelData) {
    const rng = new SeededRNG(`${this.levelDef.seed}-props`);
    // Plain collision line again — no extra offset needed now that the
    // floor art is generated to match it exactly (see _buildParallax).
    // Floor is now a single row flush with the bottom (see generate-chunks.mjs).
    const floorTopY = (levelData.heightTiles - 1) * TILE_SIZE;
    const order = rng.shuffle(PROP_KEYS);
    const TARGET_H = 40;
    // Props_3/4 (kiosk/terminal props) read as too small next to the rest of
    // the scaled-up comic-art scene at the shared 40px baseline — bumped up
    // on their own rather than raising TARGET_H for every prop.
    const TARGET_H_OVERRIDE = {
      [TEXTURE_KEYS.PROP_3]: 60,
      [TEXTURE_KEYS.PROP_4]: 60
    };
    let x = rng.int(60, 160);
    let i = 0;
    while (x < levelData.widthPx - 60) {
      const key = order[i % order.length];
      i++;
      const img = this.add.image(x, floorTopY, key).setOrigin(0.5, 1).setDepth(DEPTHS.TILES + 1).setAlpha(0.9);
      img.setScale((TARGET_H_OVERRIDE[key] ?? TARGET_H) / img.height);
      x += rng.int(90, 220);
    }
  }

  _buildHazards(levelData) {
    this.hazardFences = [];
    for (const hz of levelData.hazards) {
      if (hz.type === 'electric-fence') {
        this.hazardFences.push(new ElectricFence(this, hz.x, hz.y, hz.tileSize, hz.length ?? 1));
      }
    }
  }

  _buildPickups(levelData) {
    this.pickupsGroup = this.physics.add.staticGroup();
    this.chipsCollected = 0;
    this.chipsTotal = levelData.pickups.filter((p) => p.type === 'chip').length;
    const PICKUP_TEXTURES = {
      chip: TEXTURE_KEYS.PICKUP_CHIP,
      ammo: TEXTURE_KEYS.PICKUP_AMMO,
      health: TEXTURE_KEYS.PICKUP_HEALTH
    };
    // Pickup art is ~210-230px native (icon-in-a-frame style) — scale down
    // to a small collectible size. Bumped along with the player/enemy scale
    // increases (roughly the same ~2.8x total) so pickups don't end up
    // looking tiny next to now much-bigger characters.
    const PICKUP_SCALE = 0.252;
    for (const p of levelData.pickups) {
      const texture = PICKUP_TEXTURES[p.type] ?? TEXTURE_KEYS.PICKUP_HEALTH;
      const spr = this.pickupsGroup.create(p.x, p.y, texture);
      spr.setScale(PICKUP_SCALE);
      spr.refreshBody();
      spr.setDepth(DEPTHS.PICKUPS);
      spr.setData('type', p.type);
      this.tweens.add({ targets: spr, y: p.y - 6, duration: 800, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    }
  }

  _buildExit(levelData) {
    // Endless mode has no exit at all — the run only ends by dying (see
    // _onPlayerDied), so there's nothing to build here.
    if (this.levelDef.endless) return;

    // No physical exit door — reaching the right edge of the level
    // completes it (see update()'s edge check) instead of overlapping a
    // door prop. exitPoint.x (2 tiles shy of the level's true right edge,
    // see ChunkStitcher.js) is reused as that trigger line, marked with a
    // plain in-world sign so there's still a visible "head here" target.
    this.exitX = levelData.exitPoint.x;
    this.exitActive = !levelData.boss;
    const floorTopY = (levelData.heightTiles - 1) * TILE_SIZE;
    this.exitSign = this.add
      .text(this.exitX, floorTopY - 8, '→ EXIT', {
        fontFamily: 'monospace',
        fontSize: '12px',
        fontStyle: 'bold',
        color: this.exitActive ? '#2f8f46' : '#552233'
      })
      .setOrigin(0.5, 1)
      .setDepth(DEPTHS.HAZARDS);
  }

  _setupColliders() {
    this.physics.add.collider(this.player, this.solidGroup);
    this.physics.add.collider(this.player, this.platformGroup, null, (player, platform) =>
      player.body.velocity.y >= 0 && player.body.bottom - player.body.velocity.y / 60 <= platform.body.top + 6
    );

    // Flying enemies (allowGravity: false) manage their own y directly for
    // patrol/chase bobbing and never intentionally touch the ground — colliding
    // them with solid tiles fights that manual movement and can shove them
    // through the floor. Only grounded enemies collide with solid geometry.
    this.physics.add.collider(this.enemiesGroup, this.solidGroup, null, (enemyObj) => enemyObj.body.allowGravity);
    this.physics.add.collider(this.enemiesGroup, this.platformGroup, null, (enemyObj, platform) =>
      enemyObj.body.allowGravity &&
      enemyObj.body.velocity.y >= 0 &&
      enemyObj.body.bottom - enemyObj.body.velocity.y / 60 <= platform.body.top + 6
    );

    this.physics.add.collider(this.playerProjectiles.group, this.solidGroup, (proj) =>
      this.playerProjectiles.recycle(proj)
    );
    this.physics.add.collider(this.enemyProjectiles.group, this.solidGroup, (proj) =>
      this.enemyProjectiles.recycle(proj)
    );

    this.hitboxManager = new HitboxManager(this);
    this.hitboxManager.watchProjectiles(this.playerProjectiles, this.enemiesGroup, (enemy, dmg) =>
      enemy.takeDamage(dmg)
    );
    this.hitboxManager.watchProjectiles(this.enemyProjectiles, this.player, (player, dmg, proj) =>
      player.takeDamage(dmg, proj.x)
    );

    this.physics.add.overlap(this.player, this.spikeGroup, (player, spike) =>
      player.takeDamage(spike.getData('damage') ?? 1, spike.x)
    );

    this.physics.add.overlap(this.player, this.enemiesGroup, (player, enemy) => {
      if (!enemy.alive) return;
      const dmg = enemy.config.contactDamage;
      if (dmg > 0) player.takeDamage(dmg, enemy.x);
    });

    for (const fence of this.hazardFences) {
      this.physics.add.overlap(this.player, fence, (player, seg) => {
        const dmg = seg.getData('damage');
        if (dmg > 0) player.takeDamage(dmg, seg.x);
      });
    }

    this.physics.add.overlap(this.player, this.pickupsGroup, (player, pickup) => {
      const type = pickup.getData('type');
      if (type === 'health') {
        player.heal(2);
      } else if (type === 'chip') {
        this.chipsCollected += 1;
        this.score += 50;
        this.hudScoreText?.setText(`SCORE ${this.score}`);
        this.hudChipsText?.setText(`$ ${this.chipsCollected}/${this.chipsTotal}`);
      } else if (type === 'ammo') {
        player.refillAmmo();
      }
      pickup.destroy();
    });
  }

  _buildHud() {
    // Portrait frame (top-left) — char.png is already a framed, bordered
    // headshot (unlike the old main_large.png full-body art, which had to be
    // squeezed into a tiny square), so it drops straight in with no extra
    // background box behind it.
    this.add
      .image(28, 28, TEXTURE_KEYS.PLAYER_PORTRAIT)
      .setScale(52 / 626)
      .setScrollFactor(0)
      .setDepth(DEPTHS.UI);

    // Segmented HP bar, next to the portrait
    this.hudHpBar = { x: 62, y: 12, width: 150, height: 14 };
    this.hudHpGraphics = this.add.graphics().setScrollFactor(0).setDepth(DEPTHS.UI);

    this.hudScoreText = this.add
      .text(62, 30, 'SCORE 0', { fontFamily: 'monospace', fontSize: '11px', fontStyle: 'bold', color: INK })
      .setScrollFactor(0)
      .setDepth(DEPTHS.UI);

    this.hudChipsText = this.add
      .text(62, 44, `$ ${this.chipsCollected}/${this.chipsTotal}`, {
        fontFamily: 'monospace',
        fontSize: '11px',
        fontStyle: 'bold',
        color: INK
      })
      .setScrollFactor(0)
      .setDepth(DEPTHS.UI);

    this.add
      .image(75, 62, TEXTURE_KEYS.HUD_GUN_ICON)
      .setScale(22 / 250)
      .setScrollFactor(0)
      .setDepth(DEPTHS.UI);
    this.hudAmmoText = this.add
      .text(90, 56, `AMMO ${this.player.ammo}/${this.player.maxAmmo}`, {
        fontFamily: 'monospace',
        fontSize: '11px',
        fontStyle: 'bold',
        color: INK
      })
      .setScrollFactor(0)
      .setDepth(DEPTHS.UI);

    this.hudTimer = this.add
      .text(this.scale.width / 2, 8, '00:00', {
        fontFamily: 'monospace',
        fontSize: '11px',
        fontStyle: 'bold',
        color: INK
      })
      .setOrigin(0.5, 0)
      .setScrollFactor(0)
      .setDepth(DEPTHS.UI);

    // Objective banner (top-right) — endless mode has no fixed objective,
    // so this slot doubles as a live distance readout instead (see
    // update()).
    const objective = this.levelDef.endless
      ? 'DISTANCE 0m'
      : this.levelDef.boss
        ? `DEFEAT THE ${this.levelDef.boss.replace(/-/g, ' ').toUpperCase()}`
        : 'REACH THE EDGE';
    const bannerW = 190;
    this.add
      .rectangle(this.scale.width - 16 - bannerW / 2, 18, bannerW, 30, PAPER_NUM, 0.9)
      .setStrokeStyle(1, INK_NUM)
      .setScrollFactor(0)
      .setDepth(DEPTHS.UI);
    this.add
      .text(this.scale.width - 16 - bannerW + 8, 8, this.levelDef.name.toUpperCase(), {
        fontFamily: 'monospace',
        fontSize: '7px',
        color: MUTED
      })
      .setScrollFactor(0)
      .setDepth(DEPTHS.UI + 1);
    this.hudObjectiveText = this.add
      .text(this.scale.width - 16 - bannerW + 8, 20, `◆ ${objective}`, {
        fontFamily: 'monospace',
        fontSize: '8px',
        fontStyle: 'bold',
        color: INK
      })
      .setScrollFactor(0)
      .setDepth(DEPTHS.UI + 1);
  }

  _drawHpBar(hp, maxHp) {
    const { x, y, width, height } = this.hudHpBar;
    const gap = 2;
    const segW = (width - gap * (maxHp - 1)) / maxHp;
    const g = this.hudHpGraphics;
    g.clear();
    for (let i = 0; i < maxHp; i++) {
      const segX = x + i * (segW + gap);
      g.fillStyle(i < hp ? COLORS.danger : 0xe6e6e3, 1);
      g.fillRect(segX, y, segW, height);
      g.lineStyle(1, INK_NUM, 1);
      g.strokeRect(segX, y, segW, height);
    }
  }

  // -- event handlers ----------------------------------------------------

  _onHpChanged(hp, maxHp) {
    this._drawHpBar(Math.max(0, hp), maxHp);
  }

  _onAmmoChanged(ammo, maxAmmo) {
    this.hudAmmoText?.setText(`AMMO ${ammo}/${maxAmmo}`);
    this.hudAmmoText?.setColor(ammo === 0 ? '#ff3355' : INK);
  }

  _onEnemyDied(enemy) {
    this.score += enemy.config.boss ? 500 : 100;
    this.hudScoreText?.setText(`SCORE ${this.score}`);
    this.enemies = this.enemies.filter((e) => e !== enemy);
    if (this.boss && enemy === this.boss) {
      this.exitActive = true;
      this.exitSign?.setColor('#2f8f46');
      this.hudObjectiveText?.setText('◆ HEAD TO THE EDGE');
      this.cameras.main.flash(200, 255, 255, 255);
    }
  }

  _onPlayerDied() {
    this.paused = true;
    this.physics.world.pause();
    const { width, height } = this.scale;
    const isEndless = !!this.levelDef.endless;
    // Endless gets a taller panel: two extra stat lines, plus a third
    // (LEADERBOARD) button that a normal level's panel has no room for.
    const panelH = isEndless ? 204 : 150;
    const top = height / 2 - panelH / 2;

    this.add
      .rectangle(width / 2, height / 2, 260, panelH, PAPER_NUM, 0.95)
      .setStrokeStyle(2, INK_NUM)
      .setScrollFactor(0)
      .setDepth(DEPTHS.UI + 1);

    this.add
      .text(width / 2, top + 26, isEndless ? 'RUN OVER' : 'SYSTEM FAILURE', {
        fontFamily: 'monospace',
        fontSize: '18px',
        fontStyle: 'bold',
        color: '#c9302c'
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(DEPTHS.UI + 2);

    let isNewBest = false;
    if (isEndless) {
      // Endless mode's "completion" is death, not reaching an exit — this
      // is where its best-run stat gets recorded, reusing the same
      // per-levelId save data every campaign level uses. Points (kills +
      // chips, same as every other level) are the headline stat here;
      // distance is just a secondary "how far" readout, not itself a
      // tracked best.
      const distance = Math.floor(this.player.x / TILE_SIZE);
      const prevBest = SaveManager.getLevelState('endless').bestScore ?? 0;
      const entry = SaveManager.recordResult('endless', { score: this.score });
      isNewBest = this.score > prevBest;
      this.add
        .text(width / 2, top + 50, `SCORE ${this.score}   BEST ${entry.bestScore}`, {
          fontFamily: 'monospace',
          fontSize: '11px',
          fontStyle: 'bold',
          color: INK
        })
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(DEPTHS.UI + 2);
      this.add
        .text(width / 2, top + 66, `DISTANCE ${distance}m`, {
          fontFamily: 'monospace',
          fontSize: '9px',
          color: MUTED
        })
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(DEPTHS.UI + 2);
    }

    let y = isEndless ? top + 94 : top + 84;
    const makeButton = (label, onClick) => {
      const box = this.add
        .rectangle(width / 2, y, 190, 26, PAPER_NUM, 0)
        .setStrokeStyle(1, INK_NUM)
        .setScrollFactor(0)
        .setDepth(DEPTHS.UI + 2)
        .setInteractive({ useHandCursor: true });
      const label_ = this.add
        .text(width / 2, y, label, { fontFamily: 'monospace', fontSize: '11px', fontStyle: 'bold', color: INK })
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(DEPTHS.UI + 3);
      box.on('pointerover', () => {
        box.setFillStyle(INK_NUM, 1);
        label_.setColor('#ffffff');
      });
      box.on('pointerout', () => {
        box.setFillStyle(PAPER_NUM, 0);
        label_.setColor(INK);
      });
      box.on('pointerdown', onClick);
      y += 34;
      return box;
    };
    // Waits on an explicit click rather than auto-restarting after a timer,
    // so a dead player isn't dropped straight back into a fresh attempt
    // without a moment to see what killed them / choose to bail out instead.
    makeButton('↻  RETRY', () => this.scene.restart({ levelId: this.levelId }));
    makeButton('☰  LEVEL SELECT', () => this.scene.start('LevelSelect'));
    if (isEndless) {
      makeButton('★  LEADERBOARD', () => showLeaderboardOverlay(this));
    }

    if (isEndless && isNewBest && LEADERBOARD_ENABLED) {
      this._showInitialsEntry(this.score);
    }
  }

  /** Classic arcade-style 3-letter high-score entry: ↑↓ cycles the letter, ←→ moves slots, ENTER/SPACE confirms. */
  _showInitialsEntry(score) {
    const { width, height } = this.scale;
    const letters = ['A', 'A', 'A'];
    let slot = 0;

    const backdrop = this.add
      .rectangle(width / 2, height / 2, width, height, 0x000000, 0.7)
      .setScrollFactor(0)
      .setDepth(DEPTHS.UI + 4);
    const panel = this.add
      .rectangle(width / 2, height / 2, 260, 140, PAPER_NUM, 1)
      .setStrokeStyle(2, INK_NUM)
      .setScrollFactor(0)
      .setDepth(DEPTHS.UI + 5);
    const title = this.add
      .text(width / 2, height / 2 - 48, 'NEW BEST! ENTER INITIALS', {
        fontFamily: 'monospace',
        fontSize: '10px',
        fontStyle: 'bold',
        color: INK,
        align: 'center',
        wordWrap: { width: 220 }
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(DEPTHS.UI + 6);

    const slotBoxes = [];
    const slotTexts = [];
    const slotW = 44;
    const startX = width / 2 - slotW;
    for (let i = 0; i < 3; i++) {
      const x = startX + i * slotW;
      const box = this.add
        .rectangle(x, height / 2, 34, 40, PAPER_NUM, 0)
        .setStrokeStyle(2, INK_NUM)
        .setScrollFactor(0)
        .setDepth(DEPTHS.UI + 6);
      const text = this.add
        .text(x, height / 2, letters[i], { fontFamily: 'monospace', fontSize: '22px', fontStyle: 'bold', color: INK })
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(DEPTHS.UI + 7);
      slotBoxes.push(box);
      slotTexts.push(text);
    }

    const hint = this.add
      .text(width / 2, height / 2 + 44, '↑↓ CHANGE   ←→ MOVE   ENTER CONFIRM', {
        fontFamily: 'monospace',
        fontSize: '8px',
        color: MUTED
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(DEPTHS.UI + 6);

    const refreshSlots = () => {
      for (let i = 0; i < 3; i++) {
        slotBoxes[i].setStrokeStyle(2, INK_NUM, i === slot ? 1 : 0.35);
        slotTexts[i].setAlpha(i === slot ? 1 : 0.4);
      }
    };
    refreshSlots();

    const cycle = (dir) => {
      const code = letters[slot].charCodeAt(0) - 65;
      letters[slot] = String.fromCharCode(((code + dir + 26) % 26) + 65);
      slotTexts[slot].setText(letters[slot]);
    };

    const onKey = (event) => {
      if (event.code === 'ArrowUp') cycle(1);
      else if (event.code === 'ArrowDown') cycle(-1);
      else if (event.code === 'ArrowLeft') {
        slot = Math.max(0, slot - 1);
        refreshSlots();
      } else if (event.code === 'ArrowRight') {
        slot = Math.min(2, slot + 1);
        refreshSlots();
      } else if (event.code === 'Enter' || event.code === 'Space') {
        confirm();
      }
    };
    this.input.keyboard.on('keydown', onKey);

    const confirm = () => {
      this.input.keyboard.off('keydown', onKey);
      const group = [backdrop, panel, title, hint, ...slotBoxes, ...slotTexts];
      for (const o of group) o.destroy();
      submitScore(letters.join(''), score).then(() => showLeaderboardOverlay(this));
    };
  }


  _onPlayerFell() {
    if (!this.player.alive) return;
    this.player.takeDamage(1, this.player.x);
    if (this.player.alive) {
      // respawnAt (not a plain setPosition) applies the same surface-
      // landing correction used at level start — otherwise every
      // fall-respawn would re-embed the player in the ground, immediately
      // falling through again (see Player.js's _landOnSurface).
      this.player.respawnAt(this.levelData.playerStart.x, this.levelData.playerStart.y);
      this.player.body.setVelocity(0, 0);
    }
  }

  _completeLevel() {
    this.levelComplete = true;
    const timeMs = this.time.now - this.startTime;
    const idx = LEVEL_IDS.indexOf(this.levelId);
    const nextId = LEVEL_IDS[idx + 1] ?? null;
    SaveManager.recordResult(this.levelId, {
      timeMs,
      score: this.score,
      chipsCollected: this.chipsCollected,
      chipsTotal: this.chipsTotal
    });
    if (nextId) SaveManager.unlock(nextId);
    this.scene.start('LevelComplete', {
      levelId: this.levelId,
      timeMs,
      score: this.score,
      chipsCollected: this.chipsCollected,
      chipsTotal: this.chipsTotal,
      nextId
    });
  }

  _togglePause() {
    this.paused = !this.paused;
    const { width, height } = this.scale;
    if (this.paused) {
      this.physics.world.pause();
      this.pauseBg = this.add
        .rectangle(width / 2, height / 2, 190, 70, PAPER_NUM, 0.95)
        .setStrokeStyle(2, INK_NUM)
        .setScrollFactor(0)
        .setDepth(DEPTHS.UI + 1);
      this.pauseText = this.add
        .text(width / 2, height / 2, 'PAUSED\nESC to resume', {
          fontFamily: 'monospace',
          fontSize: '16px',
          fontStyle: 'bold',
          color: INK,
          align: 'center'
        })
        .setOrigin(0.5)
        .setScrollFactor(0)
        .setDepth(DEPTHS.UI + 2);
    } else {
      this.physics.world.resume();
      this.pauseBg?.destroy();
      this.pauseText?.destroy();
    }
  }

  _formatElapsed() {
    const s = Math.floor((this.time.now - this.startTime) / 1000);
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return `${String(m).padStart(2, '0')}:${String(rem).padStart(2, '0')}`;
  }

  update(time, delta) {
    this.inputController.update();

    if (this.levelComplete) return;

    if (this.inputController.state.pausePressed) {
      this._togglePause();
    }
    if (this.paused) return;

    this.player.update(time, delta, this.inputController.state);
    for (const e of this.enemies) e.update(time, delta);
    for (const f of this.hazardFences) f.update(time, delta);

    const bounds = new Phaser.Geom.Rectangle(0, 0, this.levelData.widthPx, this.levelData.heightPx);
    this.playerProjectiles.update(time, bounds);
    this.enemyProjectiles.update(time, bounds);

    if (this.exitActive && this.player.x >= this.exitX) this._completeLevel();

    if (this.levelDef.endless) {
      this.hudObjectiveText?.setText(`◆ DISTANCE ${Math.floor(this.player.x / TILE_SIZE)}m`);
    }

    this.hudTimer.setText(this._formatElapsed());
  }
}
